import React, { useCallback, useMemo, memo, useState, useEffect, useRef, useContext } from 'react'
import { useForm, useFormContext, FormProvider } from "react-hook-form";
import fields from '../data/formData';
import ToggleButton from './toggleButton';
import gsap from 'gsap';
import createFormData from '../util/createFormData';
import useErroredFieldState from '../hooks/useFieldState';
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import { ScrollContext } from '../context/scrollContext';

export default function ContactForm() {

    const methods = useForm({ mode: "onTouched", reValidateMode: 'onChange', criteriaMode: 'all' });

    const [currentFormState, setCurrentFormState] = useState('form')

    const onSubmit = useCallback(async (data) => {

        try {
            const formData = createFormData('Contact', data)

            const res = await fetch("/", {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: new URLSearchParams(formData).toString(),
            })
            if (res.status > 299) {
                throw new Error(res.status)
            }
            setCurrentFormState('submitted')
        } catch (e) {
            console.error(e)
            setCurrentFormState('errored')
        }

    }, [])


    return (

        <FormProvider {...methods} >
            <div className='p10'>
                <FormViews currentFormState={currentFormState} onSubmit={onSubmit} />
            </div>
        </FormProvider>
    )
}



const FormViews = memo(({ onSubmit, currentFormState }) => {
    const { scrollActions: { update } } = useContext(ScrollContext)


    const FormState = useMemo(() => ({
        'form': Form,
        'submitted': Submitted,
        'errored': Errored

    }), [])

    const CurrentFormWrapper = useMemo(() => {
        return FormState[currentFormState]
    }, [currentFormState, FormState])


    return (
        <SwitchTransition>
            <CSSTransition onEnter={update} key={currentFormState} timeout={500} classNames='fade'>
                <CurrentFormWrapper onSubmit={onSubmit} />
            </CSSTransition>
        </SwitchTransition>
    )
})

const Form = memo(({ onSubmit }) => {
    const { register, handleSubmit, setValue } = useFormContext();


    return (
        <form onSubmit={handleSubmit(onSubmit)} name="Contact" method="POST" data-netlify="true">
            <input type="hidden" name="form-name" value="Contact" />
            <FormFields fields={fields} register={register} setValue={setValue} />
            <SubmitButton />
        </form>
    )
})

const Submitted = memo(() => {
    return (
        <p className='h3 max-850 ma text-center'>Thanks for submitting we'll get back to you soon</p>
    )
})

const Errored = memo(() => {
    return (
        <p className='h3 max-850 ma text-center'>Sorry we encountered an error.</p>
    )
})


const SubmitButton = () => {
    return (
        <div className='text-light flex justify-c pt40 align-center w-100'>
            <button className='button b1 submit-button text-light hover-button'>
                <div className='text-wrapper'>
                    <span>Submit {"   "}</span>
                    <span>Send It</span>
                </div>
            </button>
        </div>
    )
}


const FormFields = memo(({ fields, register, setValue, className, ...props }) => {

    const FieldStyle = useMemo(() => ({
        'text': TextField,
        'toggle': ToggleField,
        'textarea': TextArea
    }), [])

    return (
        <div className={'grid grid-2 gap-20 border-box max-850 ma  ' + className} {...props}>
            {fields.map((field, index) => {
                const Field = FieldStyle[field.type]
                return <Field key={index} setValue={setValue} field={field} register={register} />
            })}
        </div>
    )
})


const TextField = ({ field, register }) => {
    const [open, setOpen] = useState(false)

    const { error, isDirty } = useErroredFieldState(field.name)
    return (
        <div className={'pos-rel text-light  ' + field.className}>
            <p style={{ transform: (open ? `translateY(-30%)` : `translateY(100%)`) }} className='m0 text-light input-label  '>{field.name}</p>
            <input className={'w-100 border-box input pb20 bb1 input-text text-light  ' + (open ? 'active ' : '') + ((error && isDirty) ? 'error' : '')} onFocus={() => setOpen(true)} placeholder={field.placeholder} {...register(field.name, { ...field.options })} />
        </div>
    )
}


const TextArea = ({ field, register }) => {

    const [open, setOpen] = useState(false)




    return (
        <div className={'pos-rel text-light ' + field.className}>
            <p style={{ transform: (open ? `translateY(-30%)` : `translateY(100%)`) }} className='m0 text-light input-label  '>{field.name}</p>
            <textarea className={'w-100 border-box input text-area pb20 bb1 input-text text-light  ' + (open ? 'active' : '')} onFocus={() => setOpen(true)} placeholder={field.placeholder} {...register(field.name, { ...field.options })} />
        </div>
    )
}

const ToggleField = ({ field, register, setValue }) => {

    const [currentValue, setCurrentValue] = useState(field.nested.defaultValue)
    const fields = useRef(null)

    const toggleValue = useCallback((e) => {
        e.preventDefault()
        setCurrentValue((state) => !state)
    }, [])

    const animateFieldIn = useCallback(() => {
        const tl = gsap.timeline({})
        const ref = fields.current
        tl.fromTo(ref, { height: '0px', }, { height: 'auto', duration: 0.4, ease: 'ease' })
        tl.to(ref, { opacity: 1, duration: 0.3, ease: 'ease' })
    }, [])

    const animateFieldOut = useCallback(() => {
        const tl = gsap.timeline()
        const ref = fields.current
        tl.to(ref, { opacity: 0, duration: 0.3, ease: 'ease' })
        tl.to(ref, { height: '0px', duration: 0.4, ease: 'ease' })
    }, [])


    useEffect(() => {
        setValue(field.name, currentValue)
        if (currentValue) {
            return animateFieldIn()
        } else {
            animateFieldOut()
        }
    }, [currentValue, animateFieldIn, animateFieldOut, setValue, field.name])

    return (
        <div className='col-span-2'>
            <ToggleButtonField register={register} toggleClassName={(currentValue ? 'active' : '')} field={field} className={field.className} onClick={toggleValue} />
            <div ref={fields} style={{ height: `0px` }} className='overflow-hidden col-span-2 '>
                <FormFields className={'pt30 ' + field.nested.nestedClassName} register={register} fields={field.nested.fields} />
            </div>
        </div>
    )
}

const ToggleButtonField = ({ field, register, toggleClassName, className, ...props }) => {
    return (
        <>
            <input {...register(field.name, { default: field.nested.defaultValue })} hidden />
            <button className={'button text-light bb1 w-100 pb20 pt30 flex justify-between  ' + className} {...props}>
                <span className='text-light input    '>{field.name}</span>
                <ToggleButton className={toggleClassName} />
            </button>
        </>
    )
}
